// src/features/curatedLists/slices/curatedListsSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  addAppContentToList,
  addItemToList,
  addMultipleItems,
  createList,
  createListWithAppContent,
  deleteItemFromList,
  deleteMultipleItems,
  fetchMyCuratedLists,
  fetchPaginatedCuratedLists,
  fetchUrlMetadata,
  fetchUserBookmarkedLists,
  getListById,
  getListByUsernameAndSlug,
  reorderListItems,
  toggleListBookmark,
  toggleListFeatured,
  fetchFeaturedLists,
  fetchPaginatedFeaturedLists,
  updateList,
  updateListMetadata,
} from '../actions/curatedListsActions';
import {
  CuratedList,
  CuratedListSimple,
  CuratedListsState,
  ListItem,
  PendingListItem,
  UrlMetadata,
} from '../types/curatedLists.types';

// ===== INITIAL STATE =====
const initialState: CuratedListsState = {
  // Current list
  currentList: null,

  // List creation/editing
  pendingItems: [],
  listTitle: '',

  // User's lists
  userLists: [],

  // Featured lists
  featuredLists: [],        // For homepage (simple array)
  featuredListsLastFetched: null, // Timestamp for cache invalidation
  paginatedFeaturedLists: [], // For "View All" page (paginated items)
  featuredCurrentPage: 1,
  featuredTotalCount: 0,
  featuredHasMore: false,
  isFetchingFeaturedLists: false,
  isFetchingPaginatedFeatured: false,
  featuredError: null,

  // Loading states
  isCreating: false,
  isFetchingMetadata: false,
  isLoading: false,
  isAddingContent: false,
  isFetchingUserLists: false,

  // Messages
  error: null,
  successMessage: null,
  lastAddedItem: null,

  browseLists: [],
  browseCurrentPage: 1,
  browseTotalCount: 0,
  browseHasMore: true,
  isFetchingBrowseLists: false,
  browseError: null,
};

// ===== TYPE HELPERS =====
type LoadingStateKey =
  | 'isCreating'
  | 'isFetchingMetadata'
  | 'isLoading'
  | 'isAddingContent'
  | 'isFetchingUserLists'
  | 'isFetchingBrowseLists'
  | 'isFetchingFeaturedLists'
  | 'isFetchingPaginatedFeatured';

// ===== HELPER FUNCTIONS =====
const handlePending = (
  state: CuratedListsState,
  loadingKey: LoadingStateKey
) => {
  state[loadingKey] = true;
  
  // Clear the appropriate error
  if (loadingKey === 'isFetchingBrowseLists') {
    state.browseError = null;
  } else if (loadingKey === 'isFetchingFeaturedLists' || loadingKey === 'isFetchingPaginatedFeatured') {
    state.featuredError = null;
  } else {
    state.error = null;
  }
};

const handleFulfilled = (
  state: CuratedListsState,
  loadingKey: LoadingStateKey
) => {
  state[loadingKey] = false;
};

const handleRejected = (
  state: CuratedListsState,
  action: any,
  loadingKey: LoadingStateKey
) => {
  state[loadingKey] = false;
  
  // Set the appropriate error
  if (loadingKey === 'isFetchingBrowseLists') {
    state.browseError = action.payload as string;
  } else if (loadingKey === 'isFetchingFeaturedLists' || loadingKey === 'isFetchingPaginatedFeatured') {
    state.featuredError = action.payload as string;
  } else {
    state.error = action.payload as string;
  }
};

// Helper to update current list items
const updateCurrentListItems = (
  state: CuratedListsState,
  items: ListItem[]
) => {
  if (state.currentList) {
    state.currentList.items = items;
    state.currentList.total_items = items.length;
  }
};

// Helper to add item to current list
const addItemToCurrentList = (state: CuratedListsState, item: ListItem) => {
  if (state.currentList) {
    if (!state.currentList.items) {
      state.currentList.items = [];
    }
    state.currentList.items.push(item);
    state.currentList.total_items = (state.currentList.total_items || 0) + 1;
  }
};

// Helper to remove item from current list
const removeItemFromCurrentList = (
  state: CuratedListsState,
  itemId: number
) => {
  if (state.currentList && state.currentList.items) {
    state.currentList.items = state.currentList.items.filter(
      item => item.id !== itemId
    );
    state.currentList.total_items = Math.max(
      0,
      (state.currentList.total_items || 0) - 1
    );
  }
};

// Helper to clear messages
const clearMessages = (state: CuratedListsState) => {
  state.error = null;
  state.successMessage = null;
  state.lastAddedItem = null;
};

// ===== SLICE DEFINITION =====
const curatedListsSlice = createSlice({
  name: 'curatedLists',
  initialState,
  reducers: {
    // === List Creation & Editing ===
    setListTitle: (state, action: PayloadAction<string>) => {
      if (!state.listTitle) {
        state.listTitle = action.payload;
      }
    },

    addPendingItem: (
      state,
      action: PayloadAction<Omit<PendingListItem, 'metadata'>>
    ) => {
      state.pendingItems.push({
        ...action.payload,
        metadata: null,
      });
    },

    updatePendingItemMetadata: (
      state,
      action: PayloadAction<{ tempId: number; metadata: UrlMetadata }>
    ) => {
      const item = state.pendingItems.find(
        i => i.tempId === action.payload.tempId
      );
      if (item) {
        item.metadata = action.payload.metadata;
        if (!item.title && action.payload.metadata.title) {
          item.title = action.payload.metadata.title;
        }
      }
    },

    updatePendingItemNote: (
      state,
      action: PayloadAction<{ tempId: number; note: string }>
    ) => {
      const item = state.pendingItems.find(i => i.tempId === action.payload.tempId);
      if (item) {
        item.note = action.payload.note;
      }
    },

    updatePendingItemDescription: (
      state,
      action: PayloadAction<{ tempId: number; description: string }>
    ) => {
      const item = state.pendingItems.find(i => i.tempId === action.payload.tempId);
      if (item) {
        item.description = action.payload.description;
      }
    },

    removePendingItem: (state, action: PayloadAction<number>) => {
      state.pendingItems = state.pendingItems.filter(
        item => item.tempId !== action.payload
      );
    },

    reorderPendingItems: (state, action: PayloadAction<number[]>) => {
      const orderedIds = action.payload;
      const reorderedItems: PendingListItem[] = [];

      // Only reorder if ALL items are found
      const allFound = orderedIds.every(id =>
        state.pendingItems.some(i => i.tempId === id)
      );

      if (!allFound) {
        return; // Keep original order
      }

      orderedIds.forEach(id => {
        const item = state.pendingItems.find(i => i.tempId === id);
        if (item) {
          reorderedItems.push(item);
        }
      });

      state.pendingItems = reorderedItems;
    },

    // === List Management ===
    setCurrentList: (state, action: PayloadAction<CuratedList>) => {
      state.currentList = action.payload;
    },

    updateListItems: (state, action: PayloadAction<ListItem[]>) => {
      updateCurrentListItems(state, action.payload);
    },

    // === User Lists ===
    setUserLists: (state, action: PayloadAction<CuratedListSimple[]>) => {
      state.userLists = action.payload;
    },

    // === Featured Lists ===
    resetFeaturedLists: (state) => {
      state.featuredLists = [];
      state.paginatedFeaturedLists = [];
      state.featuredCurrentPage = 1;
      state.featuredTotalCount = 0;
      state.featuredHasMore = false;
      state.featuredError = null;
      state.featuredListsLastFetched = null;
    },

    clearFeaturedError: (state) => {
      state.featuredError = null;
    },

    // === Messages ===
    clearLastAddedItem: state => {
      state.lastAddedItem = null;
    },

    clearSuccessMessage: state => {
      state.successMessage = null;
    },

    clearCurationMessages: state => {
      clearMessages(state);
    },

    // === Analytics ===
    incrementListViewCount: (state, action: PayloadAction<number>) => {
      if (state.currentList && state.currentList.id === action.payload) {
        state.currentList.view_count = (state.currentList.view_count || 0) + 1;
      }
    },

    updateListBookmarkStatus: (
      state,
      action: PayloadAction<{
        listId: number;
        is_bookmarked: boolean;
        total_bookmarks: number;
      }>
    ) => {
      const { listId, is_bookmarked, total_bookmarks } = action.payload;

      if (state.currentList && state.currentList.id === listId) {
        state.currentList.is_bookmarked = is_bookmarked;
        state.currentList.total_bookmarks = total_bookmarks;
      }

      const featuredMatch = state.featuredLists.find(l => l.id === listId);
      if (featuredMatch) {
        featuredMatch.is_bookmarked = is_bookmarked;
        featuredMatch.total_bookmarks = total_bookmarks;
      }

      const paginatedMatch = state.paginatedFeaturedLists.find(l => l.id === listId);
      if (paginatedMatch) {
        paginatedMatch.is_bookmarked = is_bookmarked;
        paginatedMatch.total_bookmarks = total_bookmarks;
      }
    },

    // === Reset ===
    resetListState: state => ({
      ...initialState,
      userLists: state.userLists, // Preserve user lists
    }),

    // === UI State ===
    setIsCreating: (state, action: PayloadAction<boolean>) => {
      state.isCreating = action.payload;
    },

    setIsFetchingMetadata: (state, action: PayloadAction<boolean>) => {
      state.isFetchingMetadata = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearError: state => {
      state.error = null;
    },

    resetBrowseLists: (state) => {
      state.browseLists = [];
      state.browseCurrentPage = 1;
      state.browseTotalCount = 0;
      state.browseHasMore = true;
      state.browseError = null;
    },
    
    setBrowseCurrentPage: (state, action: PayloadAction<number>) => {
      state.browseCurrentPage = action.payload;
    },
  },
  extraReducers: builder => {
    // === LIST CRUD ACTIONS ===
    builder
      .addCase(createList.pending, state => handlePending(state, 'isCreating'))
      .addCase(createList.fulfilled, (state, action) => {
        handleFulfilled(state, 'isCreating');
        state.currentList = action.payload;
        state.pendingItems = [];
      })
      .addCase(createList.rejected, (state, action) =>
        handleRejected(state, action, 'isCreating')
      );

    builder
      .addCase(getListById.pending, state => handlePending(state, 'isLoading'))
      .addCase(getListById.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        state.currentList = action.payload;
      })
      .addCase(getListById.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(getListByUsernameAndSlug.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(getListByUsernameAndSlug.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        state.currentList = action.payload;
      })
      .addCase(getListByUsernameAndSlug.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(updateList.pending, state => handlePending(state, 'isCreating'))
      .addCase(updateList.fulfilled, (state, action) => {
        handleFulfilled(state, 'isCreating');
        state.currentList = action.payload;
        state.pendingItems = [];
      })
      .addCase(updateList.rejected, (state, action) =>
        handleRejected(state, action, 'isCreating')
      );

    builder
      .addCase(updateListMetadata.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(updateListMetadata.fulfilled, state =>
        handleFulfilled(state, 'isLoading')
      )
      .addCase(updateListMetadata.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    // === LIST ITEM ACTIONS ===
    builder
      .addCase(addItemToList.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(addItemToList.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        addItemToCurrentList(state, action.payload);
      })
      .addCase(addItemToList.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(deleteItemFromList.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(deleteItemFromList.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        removeItemFromCurrentList(state, action.payload);
      })
      .addCase(deleteItemFromList.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(addMultipleItems.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(addMultipleItems.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        action.payload.forEach(item => addItemToCurrentList(state, item));
      })
      .addCase(addMultipleItems.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(deleteMultipleItems.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(deleteMultipleItems.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        action.payload.forEach(itemId =>
          removeItemFromCurrentList(state, itemId)
        );
      })
      .addCase(deleteMultipleItems.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(reorderListItems.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(reorderListItems.fulfilled, state =>
        handleFulfilled(state, 'isLoading')
      )
      .addCase(reorderListItems.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    // === METADATA ACTIONS ===
    builder
      .addCase(fetchUrlMetadata.pending, state =>
        handlePending(state, 'isFetchingMetadata')
      )
      .addCase(fetchUrlMetadata.fulfilled, (state, action) => {
        handleFulfilled(state, 'isFetchingMetadata');
        // Update metadata for the pending item
        if (action.payload.tempId && action.payload.metadata) {
          const item = state.pendingItems.find(
            i => i.tempId === action.payload.tempId
          );
          if (item) {
            item.metadata = action.payload.metadata;
          }
        }
      })
      .addCase(fetchUrlMetadata.rejected, (state, action) =>
        handleRejected(state, action, 'isFetchingMetadata')
      );

    // === USER LISTS ACTIONS ===
    builder
      .addCase(fetchMyCuratedLists.pending, state =>
        handlePending(state, 'isFetchingUserLists')
      )
      .addCase(fetchMyCuratedLists.fulfilled, (state, action) => {
        handleFulfilled(state, 'isFetchingUserLists');
        state.userLists = action.payload;
      })
      .addCase(fetchMyCuratedLists.rejected, (state, action) =>
        handleRejected(state, action, 'isFetchingUserLists')
      );

    // === APP CONTENT ACTIONS ===
    builder
      .addCase(addAppContentToList.pending, state => {
        handlePending(state, 'isAddingContent');
        clearMessages(state);
      })
      .addCase(addAppContentToList.fulfilled, (state, action) => {
        handleFulfilled(state, 'isAddingContent');
        state.successMessage = `Added to "${action.payload.curated_list.title}"`;
        state.lastAddedItem = action.payload;

        // Update current list if applicable
        if (
          state.currentList &&
          state.currentList.id === action.payload.curated_list.id
        ) {
          const newItem: ListItem = {
            id: action.payload.id,
            url: action.payload.url,
            title: action.payload.title,
            content_type: 'other',
            position: action.payload.position,
            added_at: action.payload.added_at,
            updated_at: action.payload.added_at,
            added_by: state.currentList.creator,
            thumbnail_url: null,
            description: '',
            note: '',
            domain: null,
            app_url: action.payload.app_url,
            app_content_type: action.meta.arg.app_content_type,
            app_content_id: action.meta.arg.app_content_id,
          };

          addItemToCurrentList(state, newItem);
        }
      })
      .addCase(addAppContentToList.rejected, (state, action) =>
        handleRejected(state, action, 'isAddingContent')
      );

    builder
      .addCase(createListWithAppContent.pending, state => {
        handlePending(state, 'isCreating');
        clearMessages(state);
      })
      .addCase(createListWithAppContent.fulfilled, (state, action) => {
        handleFulfilled(state, 'isCreating');
        state.successMessage = `List "${action.payload.list.title}" created successfully`;
        state.currentList = action.payload.list;
        state.lastAddedItem = action.payload.item || null;
        state.pendingItems = [];
      })
      .addCase(createListWithAppContent.rejected, (state, action) =>
        handleRejected(state, action, 'isCreating')
      );

    // === BOOKMARK ACTIONS ===
    builder
      .addCase(fetchUserBookmarkedLists.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(fetchUserBookmarkedLists.fulfilled, state =>
        handleFulfilled(state, 'isLoading')
      )
      .addCase(fetchUserBookmarkedLists.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(toggleListBookmark.pending, state =>
        handlePending(state, 'isLoading')
      )
      .addCase(toggleListBookmark.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        if (state.currentList) {
          state.currentList.is_bookmarked = action.payload.is_bookmarked;
          state.currentList.total_bookmarks = action.payload.total_bookmarks;
        }
      })
      .addCase(toggleListBookmark.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    // === FEATURED LIST ACTIONS ===
    builder
      .addCase(toggleListFeatured.pending, state => 
        handlePending(state, 'isLoading')
      )
      .addCase(toggleListFeatured.fulfilled, (state, action) => {
        handleFulfilled(state, 'isLoading');
        
        // Update current list if it's the one being toggled
        if (state.currentList && state.currentList.id === action.payload.id) {
          state.currentList.is_featured = action.payload.is_featured;
          state.currentList.featured_at = action.payload.featured_at;
        }
        
        // Update in browse lists if present
        const browseIndex = state.browseLists.findIndex(
          l => l.id === action.payload.id
        );
        if (browseIndex !== -1) {
          state.browseLists[browseIndex] = {
            ...state.browseLists[browseIndex],
            is_featured: action.payload.is_featured,
            featured_at: action.payload.featured_at,
          };
        }
        
        // Update in user lists if present
        const userIndex = state.userLists.findIndex(
          l => l.id === action.payload.id
        );
        if (userIndex !== -1) {
          state.userLists[userIndex] = {
            ...state.userLists[userIndex],
            is_featured: action.payload.is_featured,
            featured_at: action.payload.featured_at,
          };
        }

        // Update in featured lists if present
        const featuredIndex = state.featuredLists.findIndex(
          l => l.id === action.payload.id
        );
        if (featuredIndex !== -1) {
          if (!action.payload.is_featured) {
            // Remove from featured lists if unfeatured
            state.featuredLists = state.featuredLists.filter(
              l => l.id !== action.payload.id
            );
          } else {
            // Update featured status
            state.featuredLists[featuredIndex] = {
              ...state.featuredLists[featuredIndex],
              is_featured: action.payload.is_featured,
              featured_at: action.payload.featured_at,
            };
          }
        }

        // Update in paginated featured lists if present
        const paginatedIndex = state.paginatedFeaturedLists.findIndex(
          l => l.id === action.payload.id
        );
        if (paginatedIndex !== -1) {
          if (!action.payload.is_featured) {
            state.paginatedFeaturedLists = state.paginatedFeaturedLists.filter(
              l => l.id !== action.payload.id
            );
            state.featuredTotalCount = Math.max(0, state.featuredTotalCount - 1);
          } else {
            state.paginatedFeaturedLists[paginatedIndex] = {
              ...state.paginatedFeaturedLists[paginatedIndex],
              is_featured: action.payload.is_featured,
              featured_at: action.payload.featured_at,
            };
          }
        }
      })
      .addCase(toggleListFeatured.rejected, (state, action) =>
        handleRejected(state, action, 'isLoading')
      );

    builder
      .addCase(fetchFeaturedLists.pending, state => 
        handlePending(state, 'isFetchingFeaturedLists')
      )
      .addCase(fetchFeaturedLists.fulfilled, (state, action) => {
        handleFulfilled(state, 'isFetchingFeaturedLists');
        state.featuredLists = action.payload;
        state.featuredListsLastFetched = Date.now();
        state.featuredError = null;
      })
      .addCase(fetchFeaturedLists.rejected, (state, action) =>
        handleRejected(state, action, 'isFetchingFeaturedLists')
      );

    builder
      .addCase(fetchPaginatedFeaturedLists.pending, state => 
        handlePending(state, 'isFetchingPaginatedFeatured')
      )
      .addCase(fetchPaginatedFeaturedLists.fulfilled, (state, action) => {
        handleFulfilled(state, 'isFetchingPaginatedFeatured');
        
        const { items, count, next, page } = action.payload;
        
        if (page === 1) {
          state.paginatedFeaturedLists = items;
        } else {
          state.paginatedFeaturedLists = [...state.paginatedFeaturedLists, ...items];
        }
        
        state.featuredTotalCount = count;
        state.featuredHasMore = !!next;
        state.featuredCurrentPage = page;
        state.featuredError = null;
      })
      .addCase(fetchPaginatedFeaturedLists.rejected, (state, action) =>
        handleRejected(state, action, 'isFetchingPaginatedFeatured')
      );

    // === BROWSE/PAGINATED LISTS ACTIONS ===
    builder
      .addCase(fetchPaginatedCuratedLists.pending, state => 
        handlePending(state, 'isFetchingBrowseLists')
      )
      .addCase(fetchPaginatedCuratedLists.fulfilled, (state, action) => {
        handleFulfilled(state, 'isFetchingBrowseLists');
        
        const { items, count, next } = action.payload;
        const page = action.meta.arg.page;
        
        // Always replace the lists for pagination (not infinite scroll)
        state.browseLists = items;
        
        state.browseTotalCount = count;
        state.browseHasMore = !!next;
        state.browseCurrentPage = page;
      })
      .addCase(fetchPaginatedCuratedLists.rejected, (state, action) =>
        handleRejected(state, action, 'isFetchingBrowseLists')
      );
  },
});

// ===== ACTION EXPORTS =====
export const {
  // List Creation & Editing
  setListTitle,
  addPendingItem,
  updatePendingItemMetadata,
  updatePendingItemNote,
  updatePendingItemDescription,
  removePendingItem,
  reorderPendingItems,

  // List Management
  setCurrentList,
  updateListItems,

  // User Lists
  setUserLists,

  // Featured Lists
  resetFeaturedLists,
  clearFeaturedError,

  // Messages
  clearLastAddedItem,
  clearSuccessMessage,
  clearCurationMessages,

  // Analytics
  incrementListViewCount,
  updateListBookmarkStatus,

  // Reset
  resetListState,

  // UI State
  setIsCreating,
  setIsFetchingMetadata,
  setError,
  clearError,

  resetBrowseLists,
  setBrowseCurrentPage,
} = curatedListsSlice.actions;

// ===== SELECTOR EXPORTS =====
// Core selectors
export const selectCurrentList = (state: AppState) =>
  state.curatedLists.currentList;
export const selectPendingItems = (state: AppState) =>
  state.curatedLists.pendingItems;
export const selectListTitle = (state: AppState) =>
  state.curatedLists.listTitle;
export const selectUserLists = (state: AppState) =>
  state.curatedLists.userLists;

// Featured lists selectors
export const selectFeaturedLists = (state: AppState) =>
  state.curatedLists.featuredLists;
export const selectPaginatedFeaturedLists = (state: AppState) =>
  state.curatedLists.paginatedFeaturedLists;
export const selectFeaturedCurrentPage = (state: AppState) =>
  state.curatedLists.featuredCurrentPage;
export const selectFeaturedTotalCount = (state: AppState) =>
  state.curatedLists.featuredTotalCount;
export const selectFeaturedHasMore = (state: AppState) =>
  state.curatedLists.featuredHasMore;
export const selectIsFetchingFeaturedLists = (state: AppState) =>
  state.curatedLists.isFetchingFeaturedLists;
export const selectFeaturedListsLastFetched = (state: AppState) =>
  state.curatedLists.featuredListsLastFetched;
export const selectIsFetchingPaginatedFeatured = (state: AppState) =>
  state.curatedLists.isFetchingPaginatedFeatured;
export const selectFeaturedError = (state: AppState) =>
  state.curatedLists.featuredError;

// Computed featured selectors
export const selectRecentFeaturedLists = (state: AppState) => {
  const lists = state.curatedLists.featuredLists;
  return [...lists].sort((a, b) => {
    if (!a.featured_at) return 1;
    if (!b.featured_at) return -1;
    return new Date(b.featured_at).getTime() - new Date(a.featured_at).getTime();
  });
};

// Loading state selectors
export const selectIsCreating = (state: AppState) =>
  state.curatedLists.isCreating;
export const selectIsFetchingMetadata = (state: AppState) =>
  state.curatedLists.isFetchingMetadata;
export const selectIsLoading = (state: AppState) =>
  state.curatedLists.isLoading;
export const selectIsAddingContent = (state: AppState) =>
  state.curatedLists.isAddingContent;
export const selectIsFetchingUserLists = (state: AppState) =>
  state.curatedLists.isFetchingUserLists;

// Message selectors
export const selectError = (state: AppState) => state.curatedLists.error;
export const selectSuccessMessage = (state: AppState) =>
  state.curatedLists.successMessage;
export const selectLastAddedItem = (state: AppState) =>
  state.curatedLists.lastAddedItem;

// Computed selectors
export const selectCurrentListItems = (state: AppState) =>
  state.curatedLists.currentList?.items || [];
export const selectCurrentListItemsCount = (state: AppState) =>
  state.curatedLists.currentList?.total_items || 0;
export const selectCurrentListViewCount = (state: AppState) =>
  state.curatedLists.currentList?.view_count || 0;
export const selectUserListsCount = (state: AppState) =>
  state.curatedLists.userLists.length;

// Permission selectors
export const selectIsCurrentListOwner = (state: AppState) =>
  state.curatedLists.currentList?.is_owner || false;
export const selectCanEditCurrentList = (state: AppState) =>
  state.curatedLists.currentList?.can_edit || false;
export const selectCanDeleteCurrentList = (state: AppState) =>
  state.curatedLists.currentList?.can_delete || false;

// Staff permission selectors
export const selectIsUserStaff = (state: AppState) => 
  state.user?.isStaff || false;
export const selectCanFeatureLists = (state: AppState) =>
  selectIsUserStaff(state);

// Validation selectors
export const selectIsListValidForSave = (state: AppState) =>
  !!state.curatedLists.listTitle.trim() &&
  state.curatedLists.pendingItems.length > 0;
export const selectHasPendingItems = (state: AppState) =>
  state.curatedLists.pendingItems.length > 0;

// Browse lists selectors
export const selectBrowseLists = (state: AppState) =>
  state.curatedLists.browseLists;
export const selectBrowseCurrentPage = (state: AppState) =>
  state.curatedLists.browseCurrentPage;
export const selectBrowseTotalCount = (state: AppState) =>
  state.curatedLists.browseTotalCount;
export const selectBrowseHasMore = (state: AppState) =>
  state.curatedLists.browseHasMore;
export const selectBrowseError = (state: AppState) =>
  state.curatedLists.browseError;
export const selectIsFetchingBrowseLists = (state: AppState) =>
  state.curatedLists.isFetchingBrowseLists;

export default curatedListsSlice.reducer;